import React, { useMemo, useState, useEffect, useCallback } from "react";
import type { Breakdown, Kpis, Meta, Order } from "./types";
import {
  filterOrders,
  getUniqueBrands,
  getUniqueLocations,
  getUniqueStatuses,
} from "./utils";
import { FiRefreshCw, FiSearch } from "react-icons/fi";

import { TowerSidebar } from "./components/TowerSidebar";
import type { TabKey } from "./components/TowerSidebar";
import { TowerTilesStrip } from "./components/TowerTilesStrip";
import { OrdersTable } from "./components/OrdersTable";
import { SummaryDashboard } from "./components/SummaryDashboard";
import { LateOrdersLog } from "./components/LateOrdersLog";
import { AttendanceBoard } from "./components/AttendanceBoard";
import { FootageTab } from "./components/FootageTab";
import { StoreReadinessPage } from "./storeReadiness/StoreReadinessPage";
import { RosterMaker } from "./components/RosterMaker";
import { FreshdeskTicketTab } from "./components/FreshdeskTicketTab";

type ResolveActionType =
  | "New driver assigned"
  | "Kitchen remaking"
  | "Moved to the front of the que"
  | "Order Delivered"
  | "Order Cancelled";

type TicketInfo = {
  ticketId: string;
  status: string;
  location: string;
  brand: string;
  responderId?: string | null;
  agentName?: string | null;
};

type TicketMap = Record<string, TicketInfo>;

type Props = {
  meta: Meta;
  kpis: Kpis;
  breakdown: Breakdown;
  orders: Order[];
  fetchTowerData: () => void;
  onTakeAction: (orderId: string, actionType: ResolveActionType) => void;
  userDisplayName?: string;
  userEmail?: string;
  onLogout: () => void;
};

const TAB_TITLES: Record<TabKey, string> = {
  live: "Live orders",
  summary: "Daily summary",
  late: "Late orders",
  attendance: "Attendance",
  footage: "Footage",
  readiness: "Store Readiness",
  roster: "Store Roster",
  ticket: "Log Ticket",
};

/** Orders 120+ min auto-create a Freshdesk ticket */
const LINGERING_THRESHOLD = 120;

/** Orders 55+ min (35 min breach + 20 min grace) are tucked from the main table */
const LINGERING_HIDE = 55;

/** Tabs whose header subtitle shows the feed date from meta */
const FEED_TABS: TabKey[] = ["live", "summary", "late"];

/**
 * Returns a live SAST date/time string for the header subtitle
 * on non-feed tabs (attendance, readiness, roster, ticket, footage).
 */
function headerSubtitle(activeTab: TabKey, meta: Meta): string {
  if (FEED_TABS.includes(activeTab)) {
    return `${meta?.date ?? "--"} \u00b7 updated ${meta?.lastRefresh ?? "--"}`;
  }
  try {
    const now = new Date();
    return now.toLocaleString("en-ZA", {
      timeZone: "Africa/Johannesburg",
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return new Date().toLocaleString();
  }
}

export function TowerDashboard({
  meta,
  kpis,
  breakdown,
  orders,
  fetchTowerData,
  onTakeAction,
  userDisplayName,
  userEmail,
  onLogout,
}: Props) {
  /* ------------------------------------------------------------------ */
  /*  Hash-based tab persistence                                        */
  /* ------------------------------------------------------------------ */
  const initialTab = ((): TabKey => {
    const hash = window.location.hash.replace("#", "").toLowerCase();
    if (hash.includes("resolve")) return "live";
    const validTabs: TabKey[] = [
      "live", "summary", "late", "attendance",
      "footage", "readiness", "roster", "ticket",
    ];
    const found = validTabs.find((t) => hash === t);
    return found || "live";
  })();

  const [activeTab, setActiveTab] = useState<TabKey>(initialTab);

  // Write tab to hash on change (skip "live" for a clean URL)
  useEffect(() => {
    window.location.hash = activeTab === "live" ? "" : activeTab;
  }, [activeTab]);

  /* ------------------------------------------------------------------ */
  /*  Sidebar collapse                                                   */
  /* ------------------------------------------------------------------ */
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem("ct-sidebar-collapsed") === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem("ct-sidebar-collapsed", collapsed ? "1" : "0");
    } catch { /* */ }
  }, [collapsed]);

  /* ------------------------------------------------------------------ */
  /*  Core state                                                         */
  /* ------------------------------------------------------------------ */
  const [days, setDays] = useState<any[]>([]);
  const [branchMonthly, setBranchMonthly] = useState<any | null>(null);
  const [search, setSearch] = useState("");
  const [brandFilter, setBrandFilter] = useState("ALL");
  const [locationFilter, setLocationFilter] = useState("ALL");
  const [minAgeFilter, setMinAgeFilter] = useState("ALL");
  const [minRiskFilter, setMinRiskFilter] = useState("ALL");
  const [statusFilters, setStatusFilters] = useState<string[]>([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showEnroute, setShowEnroute] = useState(false);
  const [showLingering, setShowLingering] = useState(false);

  const [ticketMap, setTicketMap] = useState<TicketMap>({});
  const [lingeringFired, setLingeringFired] = useState<Set<string>>(new Set());

  // Resolved/cancelled orders that the operator dealt with this session.
  // These are removed from Live immediately without waiting for a refresh.
  const [manuallyResolved, setManuallyResolved] = useState<Set<string>>(new Set());

  // Resolve form state
  const [resolveOrder, setResolveOrder] = useState<any | null>(null);
  const [resolveStep, setResolveStep] = useState<"ask" | "stale" | "done">("ask");
  const [resolveForm, setResolveForm] = useState({
    driverEnrouteTime: "",
    deliveredTime: "",
  });
  const [resolveSubmitting, setResolveSubmitting] = useState(false);
  const [resolveResult, setResolveResult] = useState("");

  const [syncing, setSyncing] = useState(false);

  const runSync = () => {
    if (syncing) return;
    setSyncing(true);
    fetchTowerData();
    fetchTicketStatuses();
    setTimeout(() => setSyncing(false), 900);
  };

  /* ------------------------------------------------------------------ */
  /*  Derived filter data                                                */
  /* ------------------------------------------------------------------ */
  const brands = useMemo(() => getUniqueBrands(orders), [orders]);
  const locations = useMemo(() => getUniqueLocations(orders), [orders]);
  const statuses = useMemo(() => getUniqueStatuses(orders), [orders]);

  useEffect(() => {
    setStatusFilters((prev) =>
      prev.filter((status) => statuses.includes(status))
    );
  }, [statuses]);

  const filteredOrders = useMemo(
    () =>
      filterOrders(orders, {
        search,
        brand: brandFilter,
        location: locationFilter,
        minAge: minAgeFilter,
        minRiskPercent: minRiskFilter,
        statuses: statusFilters,
      }),
    [orders, search, brandFilter, locationFilter, minAgeFilter, minRiskFilter, statusFilters]
  );

  const enrouteCount = useMemo(
    () =>
      filteredOrders.filter(
        (o) => (o.status || "").trim() === "Enroute to DropOff"
      ).length,
    [filteredOrders]
  );

  /* ------------------------------------------------------------------ */
  /*  Lingering orders (55+ min, not enroute, not resolved)              */
  /* ------------------------------------------------------------------ */
  const lingeringOrders = useMemo(() => {
    return filteredOrders.filter((o) => {
      const age = Number(o.age);
      if (!age || age < LINGERING_HIDE) return false;
      const status = (o.status || "").trim();
      if (status === "Enroute to DropOff") return false;
      const id = String(o.id || "").trim();
      if (manuallyResolved.has(id)) return false;
      return true;
    });
  }, [filteredOrders, manuallyResolved]);

  const lingeringCount = lingeringOrders.length;

  const taggedLingeringOrders = useMemo(() => {
    return lingeringOrders.map((o) => {
      const id = String(o.id || "").trim();
      const ticket = ticketMap[id];

      let ticketState = "Breached (New)";
      if (ticket) {
        ticketState = ticket.status === "resolved" ? "Resolved" : "Active Ticket";
      }

      return {
        ...o,
        ticketState,
        agentName: ticket?.agentName || null,
      };
    });
  }, [lingeringOrders, ticketMap]);

  /* ------------------------------------------------------------------ */
  /*  Visible orders (main table — hides lingering 55+ min)              */
  /* ------------------------------------------------------------------ */
  const visibleOrders = useMemo(() => {
    let list = showEnroute
      ? filteredOrders
      : filteredOrders.filter(
          (o) => (o.status || "").trim() !== "Enroute to DropOff"
        );

    // Remove orders the operator resolved/cancelled this session
    list = list.filter((o) => !manuallyResolved.has(String(o.id || "").trim()));

    // Tuck lingering orders (55+ min) out of the main table
    list = list.filter((o) => {
      const age = Number(o.age);
      if (!age || age < LINGERING_HIDE) return true;
      const status = (o.status || "").trim();
      if (status === "Enroute to DropOff") return true; // enroute stays
      return false;
    });

    return list;
  }, [filteredOrders, showEnroute, manuallyResolved]);

  const { breachedNewCount, activeTicketCount } = useMemo(() => {
    let bNew = 0;
    let aTix = 0;

    for (const o of visibleOrders) {
      const id = String(o.id || "").trim();
      const ticket = ticketMap[id];

      if (ticket && ticket.status === "open") {
        aTix++;
      } else if (!ticket) {
        bNew++;
      }
    }

    return { breachedNewCount: bNew, activeTicketCount: aTix };
  }, [visibleOrders, ticketMap]);

  const taggedOrders = useMemo(() => {
    return visibleOrders.map((o) => {
      const id = String(o.id || "").trim();
      const ticket = ticketMap[id];

      let ticketState = "Breached (New)";
      if (ticket) {
        ticketState = ticket.status === "resolved" ? "Resolved" : "Active Ticket";
      }

      return {
        ...o,
        ticketState,
        agentName: ticket?.agentName || null,
      };
    });
  }, [visibleOrders, ticketMap]);

  /* ------------------------------------------------------------------ */
  /*  Ticket polling                                                     */
  /* ------------------------------------------------------------------ */
  const fetchTicketStatuses = useCallback(async () => {
    try {
      const res = await fetch("/.netlify/functions/get-ticket-status");
      if (!res.ok) return;
      const data = await res.json();
      if (data.tickets) {
        setTicketMap(data.tickets);
      }
    } catch (e) {
      console.error("TICKET_STATUS_FETCH:", e);
    }
  }, []);

  useEffect(() => {
    fetchTicketStatuses();
    const iv = setInterval(fetchTicketStatuses, 30000);
    return () => clearInterval(iv);
  }, [fetchTicketStatuses]);

  // Detect lingering orders (120+ min) and auto-create tickets
  useEffect(() => {
    const lingering = visibleOrders.filter((o) => {
      const age = Number(o.age);
      if (!age || age < LINGERING_THRESHOLD) return false;
      const id = String(o.id || "").trim();
      if (ticketMap[id]) return false;
      if (lingeringFired.has(id)) return false;
      return true;
    });

    if (lingering.length === 0) return;

    for (const o of lingering) {
      const id = String(o.id || "").trim();
      setLingeringFired((prev) => new Set(prev).add(id));

      fetch("/.netlify/functions/create-lingering-ticket", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ order: o }),
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.created) {
            console.log(`LINGERING_AUTO: ${id} → ticket #${data.ticketId}`);
            fetchTicketStatuses();
          }
        })
        .catch((e) => console.error("LINGERING_AUTO_FAIL:", e));
    }
  }, [visibleOrders, ticketMap, lingeringFired, fetchTicketStatuses]);

  /* ------------------------------------------------------------------ */
  /*  Resolve deep-link from hash                                        */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    const hash = window.location.hash || "";
    const match = hash.match(/resolve\?order=([^&]+)/);
    if (match) {
      const orderId = decodeURIComponent(match[1]);
      const order = orders.find(
        (o) => String(o.id || "").trim() === orderId
      );
      if (order) {
        setResolveOrder(order);
        setResolveStep("ask");
      }
    }
  }, [orders]);

  const closeResolveModal = () => {
    setResolveOrder(null);
    setResolveStep("ask");
    setResolveForm({ driverEnrouteTime: "", deliveredTime: "" });
    setResolveResult("");
    window.location.hash = activeTab === "live" ? "" : activeTab;
  };

  // Submit stale order with actual times
  const submitResolve = async () => {
    if (!resolveOrder || !resolveForm.deliveredTime) return;
    setResolveSubmitting(true);
    setResolveResult("");

    try {
      const res = await fetch("/.netlify/functions/resolve-order", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          orderId: resolveOrder.id,
          customer: resolveOrder.customer,
          brand: resolveOrder.brand,
          location: resolveOrder.location,
          driverEnrouteTime: resolveForm.driverEnrouteTime,
          deliveredTime: resolveForm.deliveredTime,
          resolution: "delivered",
          resolvedBy: userDisplayName || userEmail || "operator",
        }),
      });

      const data = await res.json();
      if (data.resolved) {
        setManuallyResolved((prev) =>
          new Set(prev).add(String(resolveOrder.id).trim())
        );
        setResolveResult("Order resolved. It will be included in the daily summary.");
        setResolveStep("done");
        setTimeout(closeResolveModal, 2000);
      } else {
        setResolveResult(data.error || "Could not resolve. Try again.");
      }
    } catch (e) {
      setResolveResult("Network error. Try again.");
    } finally {
      setResolveSubmitting(false);
    }
  };

  // Cancel order -- removes from Live, does NOT add to daily summary
  const submitCancel = async () => {
    if (!resolveOrder) return;
    setResolveSubmitting(true);
    setResolveResult("");

    try {
      const res = await fetch("/.netlify/functions/resolve-order", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          orderId: resolveOrder.id,
          customer: resolveOrder.customer,
          brand: resolveOrder.brand,
          location: resolveOrder.location,
          driverEnrouteTime: "",
          deliveredTime: "",
          resolution: "cancelled",
          resolvedBy: userDisplayName || userEmail || "operator",
        }),
      });

      const data = await res.json();
      if (data.resolved) {
        setManuallyResolved((prev) =>
          new Set(prev).add(String(resolveOrder.id).trim())
        );
        setResolveResult("Order cancelled. It will not appear in the daily summary.");
        setResolveStep("done");
        setTimeout(closeResolveModal, 2000);
      } else {
        setResolveResult(data.error || "Could not cancel. Try again.");
      }
    } catch (e) {
      setResolveResult("Network error. Try again.");
    } finally {
      setResolveSubmitting(false);
    }
  };

  /* ------------------------------------------------------------------ */
  /*  Summary fetch                                                      */
  /* ------------------------------------------------------------------ */
  const fetchSummaries = async () => {
    try {
      const res = await fetch("/.netlify/functions/get-daily-summary");
      const data = await res.json();
      setDays(Array.isArray(data?.days) ? data.days : []);
      setBranchMonthly(data?.branchMonthly ?? null);
    } catch (error) {
      console.error("Summary fetch error", error);
    }
  };

  useEffect(() => {
    if (activeTab === "summary" || activeTab === "late") {
      fetchSummaries();
    }
  }, [activeTab]);

  /* ------------------------------------------------------------------ */
  /*  Layout helpers                                                     */
  /* ------------------------------------------------------------------ */
  const initials =
    userDisplayName
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase() || "U";

  const contentLeft = collapsed
    ? "var(--ct-sidebar-w-collapsed)"
    : "var(--ct-sidebar-w)";

  /* ------------------------------------------------------------------ */
  /*  Render                                                             */
  /* ------------------------------------------------------------------ */
  return (
    <div style={{ minHeight: "100vh", background: "var(--ct-bg)" }}>
      <TowerSidebar
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        collapsed={collapsed}
        setCollapsed={setCollapsed}
      />

      <div
        style={{
          marginLeft: contentLeft,
          transition: "margin-left .16s ease",
          minHeight: "100vh",
        }}
      >
        {/* TOP BAR */}
        <header
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 20,
            padding: "0 24px",
            height: 61,
            background: "var(--ct-surface)",
            borderBottom: "1px solid var(--ct-line)",
            position: "sticky",
            top: 0,
            zIndex: 30,
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontSize: 15,
                fontWeight: 650,
                letterSpacing: "-0.01em",
                whiteSpace: "nowrap",
              }}
            >
              {TAB_TITLES[activeTab]}
            </div>
            <div
              style={{
                fontSize: 12,
                color: "var(--ct-ink-3)",
                whiteSpace: "nowrap",
              }}
            >
              {headerSubtitle(activeTab, meta)}
            </div>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
              justifyContent: "flex-end",
            }}
          >
            {/* Search, enroute and lingering — only visible on Live tab */}
            {activeTab === "live" && (
              <>
                <div style={{ position: "relative" }}>
                  <FiSearch
                    size={15}
                    style={{
                      position: "absolute",
                      left: 10,
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "var(--ct-ink-3)",
                      pointerEvents: "none",
                    }}
                  />
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search orders, customers, brands"
                    style={{
                      fontFamily: "inherit",
                      fontSize: 13,
                      padding: "7px 11px 7px 31px",
                      width: 250,
                      borderRadius: 8,
                      border: "1px solid var(--ct-line)",
                      background: "var(--ct-surface)",
                      color: "var(--ct-ink)",
                      outline: "none",
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = "var(--ct-accent)";
                      e.currentTarget.style.boxShadow = "0 0 0 3px var(--ct-accent-bg)";
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = "var(--ct-line)";
                      e.currentTarget.style.boxShadow = "none";
                    }}
                  />
                </div>

                <button
                  type="button"
                  onClick={() => setShowEnroute(!showEnroute)}
                  style={{
                    fontFamily: "inherit",
                    fontSize: 13,
                    fontWeight: 500,
                    padding: "7px 13px",
                    borderRadius: 8,
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    border: `1px solid ${showEnroute ? "var(--ct-accent)" : "var(--ct-line)"}`,
                    background: showEnroute ? "var(--ct-accent-bg)" : "var(--ct-surface)",
                    color: showEnroute ? "var(--ct-accent)" : "var(--ct-ink-2)",
                  }}
                >
                  {showEnroute ? "Hide enroute" : `Show enroute (${enrouteCount})`}
                </button>

                {lingeringCount > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowLingering(!showLingering)}
                    style={{
                      fontFamily: "inherit",
                      fontSize: 13,
                      fontWeight: 600,
                      padding: "7px 13px",
                      borderRadius: 8,
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                      border: `1px solid var(--ct-warn)`,
                      background: showLingering ? "var(--ct-warn)" : "var(--ct-warn-bg, rgba(245,158,11,0.10))",
                      color: showLingering ? "#fff" : "var(--ct-warn)",
                    }}
                  >
                    {showLingering ? "Hide lingering" : `Lingering (${lingeringCount})`}
                  </button>
                )}
              </>
            )}

            <button
              onClick={runSync}
              disabled={syncing}
              title="Refresh data"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 7,
                fontFamily: "inherit",
                fontSize: 13,
                fontWeight: 500,
                padding: "7px 13px",
                borderRadius: 8,
                border: `1px solid ${syncing ? "var(--ct-accent)" : "var(--ct-line)"}`,
                background: syncing ? "var(--ct-accent-bg)" : "var(--ct-surface)",
                color: syncing ? "var(--ct-accent)" : "var(--ct-ink)",
                cursor: syncing ? "default" : "pointer",
                transition: "background .12s ease, border-color .12s ease, color .12s ease",
              }}
            >
              <FiRefreshCw
                size={14}
                style={{
                  animation: syncing ? "ct-spin .8s linear infinite" : "none",
                }}
              />
              {syncing ? "Syncing…" : "Sync"}
            </button>

            <div style={{ position: "relative" }}>
              <button
                onClick={() => setMenuOpen((v) => !v)}
                onBlur={() => setTimeout(() => setMenuOpen(false), 150)}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  border: "none",
                  background: "var(--ct-accent-bg)",
                  color: "var(--ct-accent)",
                  fontSize: 12,
                  fontWeight: 700,
                  fontFamily: "inherit",
                  cursor: "pointer",
                }}
              >
                {initials}
              </button>

              {menuOpen && (
                <div
                  style={{
                    position: "absolute",
                    right: 0,
                    marginTop: 8,
                    width: 210,
                    borderRadius: 10,
                    padding: 6,
                    background: "var(--ct-surface)",
                    border: "1px solid var(--ct-line)",
                    boxShadow: "0 8px 24px rgba(15,23,42,0.12)",
                    zIndex: 60,
                  }}
                >
                  <div style={{ padding: "6px 10px 0", fontSize: 13, fontWeight: 500 }}>
                    {userDisplayName || "User"}
                  </div>
                  <div
                    style={{
                      padding: "1px 10px 8px",
                      fontSize: 12,
                      color: "var(--ct-ink-3)",
                      wordBreak: "break-all",
                    }}
                  >
                    {userEmail}
                  </div>
                  <div style={{ height: 1, background: "var(--ct-line)", margin: "4px 0" }} />
                  <button
                    onClick={onLogout}
                    style={{
                      width: "100%",
                      textAlign: "left",
                      padding: "8px 10px",
                      fontSize: 13,
                      fontFamily: "inherit",
                      borderRadius: 7,
                      border: "none",
                      background: "transparent",
                      color: "var(--ct-ink-2)",
                      cursor: "pointer",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "var(--ct-crit-bg)";
                      e.currentTarget.style.color = "var(--ct-crit)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "transparent";
                      e.currentTarget.style.color = "var(--ct-ink-2)";
                    }}
                  >
                    Log out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* ============================================================ */}
        {/*  TAB CONTENT                                                  */}
        {/* ============================================================ */}

        {/* LIVE TAB */}
        {activeTab === "live" && (
          <main style={{ padding: "20px 24px 40px" }}>
            <TowerTilesStrip
              breachedNew={breachedNewCount}
              activeTickets={activeTicketCount}
            />
            <div style={{ marginTop: 20 }}>
              <OrdersTable orders={taggedOrders} onTakeAction={onTakeAction} />
            </div>

            {/* Lingering orders section — shown below main table when toggled */}
            {showLingering && lingeringCount > 0 && (
              <div style={{ marginTop: 32 }}>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 650,
                    color: "var(--ct-warn)",
                    marginBottom: 12,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <span
                    style={{
                      display: "inline-block",
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: "var(--ct-warn)",
                    }}
                  />
                  Lingering orders ({lingeringCount})
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 400,
                      color: "var(--ct-ink-3)",
                    }}
                  >
                    &mdash; {LINGERING_HIDE}+ min, tucked from main table
                  </span>
                </div>
                <OrdersTable
                  orders={taggedLingeringOrders}
                  onTakeAction={onTakeAction}
                />
              </div>
            )}
          </main>
        )}

        {/* SUMMARY TAB */}
        {activeTab === "summary" && <SummaryDashboard days={days} />}

        {/* LATE ORDERS TAB */}
        {activeTab === "late" && <LateOrdersLog days={days} branchMonthly={branchMonthly} />}

        {/*
          ATTENDANCE TAB — uses display:block/none for instant switching
          (the AttendanceBoard has its own module-level cache)
        */}
        <div style={{ display: activeTab === "attendance" ? "block" : "none" }}>
          <main style={{ padding: "20px 24px 40px" }}>
            <AttendanceBoard />
          </main>
        </div>

        {/* FOOTAGE TAB */}
        {activeTab === "footage" && (
          <main style={{ padding: "0 24px 40px" }}>
            <FootageTab />
          </main>
        )}

        {/*
          STORE READINESS TAB — uses display:block/none for instant switching
        */}
        <div style={{ display: activeTab === "readiness" ? "block" : "none" }}>
          <main>
            <StoreReadinessPage />
          </main>
        </div>

        {/* ROSTER TAB — conditional mount (no cache benefit) */}
        {activeTab === "roster" && (
          <main>
            <RosterMaker />
          </main>
        )}

        {/* TICKET TAB — conditional mount */}
        {activeTab === "ticket" && (
          <main>
            <FreshdeskTicketTab />
          </main>
        )}
      </div>

      {/* RESOLVE MODAL */}
      {resolveOrder && (
        <div
          onClick={closeResolveModal}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15,23,42,0.55)",
            zIndex: 100,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: 480,
              border: "1px solid var(--ct-line)",
              borderRadius: 12,
              background: "var(--ct-surface)",
              padding: 24,
              boxShadow: "0 20px 60px rgba(15,23,42,0.25)",
            }}
          >
            <div
              style={{
                fontSize: 16,
                fontWeight: 650,
                letterSpacing: "-0.01em",
                color: "var(--ct-ink)",
                marginBottom: 6,
              }}
            >
              Resolve lingering order
            </div>
            <div
              style={{
                fontSize: 13,
                color: "var(--ct-ink-2)",
                marginBottom: 20,
                lineHeight: 1.5,
              }}
            >
              {resolveOrder.id} &middot; {resolveOrder.customer} &middot; {resolveOrder.brand} &middot; {resolveOrder.location}
            </div>

            {/* Step 1: Ask what happened */}
            {resolveStep === "ask" && (
              <div>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: "var(--ct-ink)",
                    marginBottom: 14,
                  }}
                >
                  What happened with this order?
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <button
                    onClick={() => setResolveStep("stale")}
                    style={{
                      padding: "12px 16px",
                      borderRadius: 8,
                      border: "1px solid var(--ct-line)",
                      background: "var(--ct-surface)",
                      fontFamily: "inherit",
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: "pointer",
                      color: "var(--ct-ink)",
                      textAlign: "left",
                    }}
                  >
                    Stale order &mdash; enter actual times
                    <div style={{ fontSize: 12, fontWeight: 400, color: "var(--ct-ink-3)", marginTop: 3 }}>
                      The order was delivered but the sheet didn&rsquo;t update
                    </div>
                  </button>

                  <button
                    onClick={closeResolveModal}
                    style={{
                      padding: "12px 16px",
                      borderRadius: 8,
                      border: "1px solid var(--ct-line)",
                      background: "var(--ct-surface)",
                      fontFamily: "inherit",
                      fontSize: 14,
                      fontWeight: 500,
                      cursor: "pointer",
                      color: "var(--ct-ink-2)",
                      textAlign: "left",
                    }}
                  >
                    Still in progress
                    <div style={{ fontSize: 12, fontWeight: 400, color: "var(--ct-ink-3)", marginTop: 3 }}>
                      The order is still being worked on
                    </div>
                  </button>

                  <button
                    onClick={submitCancel}
                    disabled={resolveSubmitting}
                    style={{
                      padding: "12px 16px",
                      borderRadius: 8,
                      border: "1px solid var(--ct-crit-bg)",
                      background: "var(--ct-crit-bg)",
                      fontFamily: "inherit",
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: "pointer",
                      color: "var(--ct-crit)",
                      textAlign: "left",
                    }}
                  >
                    {resolveSubmitting ? "Cancelling…" : "Order cancelled"}
                    <div style={{ fontSize: 12, fontWeight: 400, color: "var(--ct-crit)", marginTop: 3, opacity: 0.7 }}>
                      Removes from Live without adding to daily summary
                    </div>
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Enter actual times for stale order */}
            {resolveStep === "stale" && !resolveResult && (
              <div>
                <div style={{ marginBottom: 14 }}>
                  <label
                    style={{
                      fontSize: 13,
                      fontWeight: 500,
                      color: "var(--ct-ink-2)",
                      display: "block",
                      marginBottom: 5,
                    }}
                  >
                    Driver enroute time (HH:MM)
                  </label>
                  <input
                    type="time"
                    value={resolveForm.driverEnrouteTime}
                    onChange={(e) =>
                      setResolveForm((f) => ({ ...f, driverEnrouteTime: e.target.value }))
                    }
                    style={{
                      width: "100%",
                      padding: "9px 12px",
                      borderRadius: 8,
                      border: "1px solid var(--ct-line)",
                      fontFamily: "inherit",
                      fontSize: 14,
                      color: "var(--ct-ink)",
                      outline: "none",
                    }}
                  />
                </div>

                <div style={{ marginBottom: 20 }}>
                  <label
                    style={{
                      fontSize: 13,
                      fontWeight: 500,
                      color: "var(--ct-ink-2)",
                      display: "block",
                      marginBottom: 5,
                    }}
                  >
                    Delivery time (HH:MM) *
                  </label>
                  <input
                    type="time"
                    value={resolveForm.deliveredTime}
                    onChange={(e) =>
                      setResolveForm((f) => ({ ...f, deliveredTime: e.target.value }))
                    }
                    style={{
                      width: "100%",
                      padding: "9px 12px",
                      borderRadius: 8,
                      border: "1px solid var(--ct-line)",
                      fontFamily: "inherit",
                      fontSize: 14,
                      color: "var(--ct-ink)",
                      outline: "none",
                    }}
                  />
                </div>

                <div style={{ display: "flex", gap: 10 }}>
                  <button
                    onClick={() => setResolveStep("ask")}
                    style={{
                      flex: 1,
                      padding: "11px",
                      borderRadius: 8,
                      border: "1px solid var(--ct-line)",
                      background: "var(--ct-surface)",
                      fontFamily: "inherit",
                      fontSize: 14,
                      fontWeight: 500,
                      cursor: "pointer",
                      color: "var(--ct-ink-2)",
                    }}
                  >
                    Back
                  </button>
                  <button
                    onClick={submitResolve}
                    disabled={resolveSubmitting || !resolveForm.deliveredTime}
                    style={{
                      flex: 2,
                      padding: "11px",
                      borderRadius: 8,
                      border: "none",
                      background: resolveForm.deliveredTime
                        ? "var(--ct-accent)"
                        : "var(--ct-surface-3)",
                      color: resolveForm.deliveredTime ? "#fff" : "var(--ct-ink-3)",
                      fontFamily: "inherit",
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: resolveForm.deliveredTime ? "pointer" : "not-allowed",
                    }}
                  >
                    {resolveSubmitting ? "Submitting…" : "Resolve order"}
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Result */}
            {resolveStep === "done" && resolveResult && (
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: resolveResult.includes("resolved") || resolveResult.includes("cancelled")
                    ? "var(--ct-ok)"
                    : "var(--ct-crit)",
                  textAlign: "center",
                  padding: "20px 0",
                }}
              >
                {resolveResult}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
